import { createServerFn } from "@tanstack/react-start";
import type { Json } from "@/integrations/supabase/types";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  canEditQuote,
  checkDeliverable,
  checkStatusTransition,
  conciergeEmailKey,
  derivePaymentState,
  type ConciergeEmailEvent,
  type ConciergeSnapshot,
} from "@/lib/concierge-workflow";

const CreateInput = z.object({
  destination: z.string().trim().min(2).max(200),
  start_date: z.string().optional().nullable(),
  end_date: z.string().optional().nullable(),
  people: z.number().int().min(1).max(50).default(1),
  budget: z.number().nonnegative().optional().nullable(),
  currency: z.string().max(6).default("EUR"),
  interests: z.array(z.string().max(60)).max(30).default([]),
  transport: z.string().max(40).optional().nullable(),
  accommodation: z.string().max(200).optional().nullable(),
  notes: z.string().max(4000).optional().nullable(),
  attachments: z
    .array(z.object({ name: z.string().max(200), url: z.string().url().max(1000) }))
    .max(10)
    .default([]),
  /** Idioma ativo do cliente (para localizar os emails do Planeamento personalizado). */
  lang: z.string().max(8).optional().nullable(),
});

/** Lê o idioma do cliente guardado em metadata (fallback: pt). */
function conciergeLangOf(metadata: unknown): string {
  const m = metadata as { lang?: unknown } | null;
  const lang = typeof m?.lang === "string" ? m.lang.slice(0, 2).toLowerCase() : "";
  return lang || "pt";
}

/** Obtém o idioma do cliente para um pedido, sem falhar o fluxo em caso de erro. */
type MetadataQueryClient = {
  from: (table: string) => {
    select: (cols: string) => {
      eq: (
        col: string,
        value: string,
      ) => { single: () => PromiseLike<{ data: { metadata?: unknown } | null }> };
    };
  };
};

async function fetchConciergeLang(client: unknown, requestId: string): Promise<string> {
  try {
    const { data } = await (client as MetadataQueryClient)
      .from("concierge_requests")
      .select("metadata")
      .eq("id", requestId)
      .single();
    return conciergeLangOf(data?.metadata ?? null);
  } catch {
    return "pt";
  }
}

export const createConciergeRequest = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => CreateInput.parse(d))
  .handler(async ({ data, context }) => {
    const { supabase, userId, claims } = context;
    const { lang: requestLang, ...insertData } = data;
    const clientLang = (requestLang ?? "pt").slice(0, 2).toLowerCase();
    const { data: row, error } = await supabase
      .from("concierge_requests")
      .insert({
        ...insertData,
        user_id: userId,
        status: "novo",
        metadata: { lang: clientLang } as Json,
      })
      .select("id")
      .single();
    if (error) throw new Error(error.message);

    const requesterEmail = (claims as { email?: string } | null)?.email ?? "";
    const origin = process.env.APP_PUBLIC_URL ?? "https://routify-tour.com";

    try {
      const { sendInternalEmail } = await import("@/lib/email/send-internal.server");
      await sendInternalEmail({
        templateName: "concierge-request",
        recipientEmail: "routify.info@gmail.com",
        idempotencyKey: `concierge-${row.id}`,
        templateData: {
          requesterEmail,
          destination: data.destination,
          startDate: data.start_date,
          endDate: data.end_date,
          people: data.people,
          budget: data.budget != null ? String(data.budget) : null,
          currency: data.currency,
          interests: (data.interests ?? []).join(", "),
          transport: data.transport,
          accommodation: data.accommodation,
          notes: data.notes,
          requestId: row.id,
        },
      });
    } catch (e) {
      console.warn("concierge email notify failed", e);
    }

    // Client-facing confirmation email — exactly once per request (idempotent key).
    if (requesterEmail) {
      try {
        const { sendInternalEmail } = await import("@/lib/email/send-internal.server");
        await sendInternalEmail({
          templateName: "concierge-confirmation",
          recipientEmail: requesterEmail,
          idempotencyKey: conciergeEmailKey("submitted" as ConciergeEmailEvent, row.id),
          templateData: {
            destination: data.destination,
            requestUrl: `${origin}/concierge/${row.id}`,
            lang: clientLang,
          },
        });
      } catch (e) {
        console.warn("concierge confirmation email failed", e);
      }
    }

    return { id: row.id };
  });

export const listMyConciergeRequests = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("concierge_requests")
      .select(
        "id,destination,start_date,end_date,status,created_at,updated_at,payment_status,price_cents,delivered_at,is_first_free,app_route_link",
      )
      .eq("user_id", context.userId)
      .is("archived_at", null)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

// Pedidos atribuídos ao colaborador atual (concierge_agent)
export const listAssignedConciergeRequests = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: isAgent } = await context.supabase.rpc("is_concierge_agent", {
      _user_id: context.userId,
    });
    const { data: isAdmin } = await context.supabase.rpc("is_admin", { _user_id: context.userId });
    if (!isAgent && !isAdmin) throw new Error("Forbidden");
    const { data, error } = await context.supabase
      .from("concierge_requests")
      .select("*")
      .eq("assigned_to", context.userId)
      .is("archived_at", null)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    const rows = data ?? [];
    if (rows.length === 0) return rows;
    const userIds = Array.from(new Set(rows.map((r) => r.user_id).filter((v): v is string => Boolean(v))));
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [{ data: profs }, emailPairs] = await Promise.all([
      supabaseAdmin.from("profiles").select("id, display_name").in("id", userIds),
      Promise.all(
        userIds.map(async (uid) => {
          const { data: u } = await supabaseAdmin.auth.admin.getUserById(uid);
          return [uid, u?.user?.email ?? null] as const;
        }),
      ),
    ]);
    const nameMap = new Map((profs ?? []).map((p) => [p.id, p.display_name]));
    const emailMap = new Map(emailPairs);
    return rows.map((r) => ({
      ...r,
      requester_email: r.user_id ? emailMap.get(r.user_id) ?? null : null,
      requester_name: r.user_id ? nameMap.get(r.user_id) ?? null : null,
    }));
  });

export const listAllConciergeRequests = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ includeArchived: z.boolean().default(false) }).parse(d ?? {}),
  )
  .handler(async ({ context, data }) => {
    const { data: isAdmin } = await context.supabase.rpc("is_admin", { _user_id: context.userId });
    if (!isAdmin) throw new Error("Forbidden");
    const query = context.supabase
      .from("concierge_requests")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(500);
    if (data.includeArchived) {
      query.not("archived_at", "is", null);
    } else {
      query.is("archived_at", null);
    }
    const { data: rowsData, error } = await query;
    if (error) throw new Error(error.message);
    const rows = rowsData ?? [];
    if (rows.length === 0) return rows;

    const userIds = Array.from(new Set(rows.map((r) => r.user_id).filter((v): v is string => Boolean(v))));
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const [{ data: profs }, emailPairs] = await Promise.all([
      supabaseAdmin.from("profiles").select("id, display_name").in("id", userIds),
      Promise.all(
        userIds.map(async (uid) => {
          const { data: u } = await supabaseAdmin.auth.admin.getUserById(uid);
          return [uid, u?.user?.email ?? null] as const;
        }),
      ),
    ]);
    const nameMap = new Map((profs ?? []).map((p) => [p.id, p.display_name]));
    const emailMap = new Map(emailPairs);

    // Orçamento ativo por pedido (para derivar o estado de pagamento no painel).
    const { data: quoteRows } = await supabaseAdmin
      .from("concierge_quotes")
      .select("concierge_request_id,status,expires_at,version")
      .in(
        "concierge_request_id",
        rows.map((r) => r.id),
      )
      .in("status", ["quoted", "checkout_created"])
      .order("version", { ascending: false });
    const quoteMap = new Map<string, { status: string; expiresAt: string | null }>();
    for (const q of quoteRows ?? []) {
      if (!quoteMap.has(q.concierge_request_id)) {
        quoteMap.set(q.concierge_request_id, { status: q.status, expiresAt: q.expires_at });
      }
    }

    return rows.map((r) => ({
      ...r,
      requester_email: r.user_id ? emailMap.get(r.user_id) ?? null : null,
      requester_name: r.user_id ? nameMap.get(r.user_id) ?? null : null,
      active_quote: quoteMap.get(r.id) ?? null,
    }));
  });

const StatusInput = z.object({
  id: z.string().uuid(),
  status: z.enum([
    "novo",
    "em_analise",
    "em_desenvolvimento",
    "concluido",
    "entregue",
    "cancelado",
  ]),
  note: z.string().max(2000).optional().default(""),
  /** Override auditado (apenas fundador) para trabalhar antes do pagamento. */
  override: z.boolean().optional().default(false),
});

export const updateConciergeStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => StatusInput.parse(d))
  .handler(async ({ data, context }) => {
    const { isAdmin } = await assertStaffForRequest(context.supabase, context.userId, data.id);

    const { data: current, error: readErr } = await context.supabase
      .from("concierge_requests")
      .select("status,payment_status,is_first_free,price_cents,delivered_at")
      .eq("id", data.id)
      .single();
    if (readErr) throw new Error(readErr.message);

    if (current.status === data.status) {
      return { ok: true as const, unchanged: true as const };
    }

    const activeQuote = await loadActiveQuote(context.supabase, data.id);
    const paymentState = derivePaymentState({
      status: current.status,
      paymentStatus: current.payment_status ?? "",
      isFirstFree: current.is_first_free,
      priceCents: current.price_cents,
      deliveredAt: current.delivered_at,
      hasDeliverable: false,
      activeQuote,
    });

    const override = Boolean(data.override) && isAdmin;
    const check = checkStatusTransition(current.status, data.status, {
      paymentState,
      manual: true,
      override,
    });
    if (!check.ok) throw new Error(check.reason);

    const { error: updErr } = await context.supabase
      .from("concierge_requests")
      .update({ status: data.status })
      .eq("id", data.id);
    if (updErr) throw new Error(updErr.message);

    await context.supabase.from("concierge_status_history").insert({
      request_id: data.id,
      from_status: current.status,
      to_status: data.status,
      changed_by: context.userId,
      note:
        [data.note || null, override ? "OVERRIDE auditado (pagamento pendente)" : null]
          .filter(Boolean)
          .join(" · ") || null,
    });

    // Notifica o cliente quando o pedido passa para "em análise"
    if (data.status === "em_analise" && current.status !== "em_analise") {
      try {
        const { data: row } = await context.supabase
          .from("concierge_requests")
          .select("user_id,destination")
          .eq("id", data.id)
          .single();
        if (row) {
          const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
          const { data: userLookup } = row.user_id
      ? await supabaseAdmin.auth.admin.getUserById(row.user_id)
      : { data: null };
          const clientEmail = userLookup?.user?.email ?? null;
          if (clientEmail) {
            const { sendInternalEmail } = await import("@/lib/email/send-internal.server");
            const origin = process.env.APP_PUBLIC_URL ?? "https://routify-tour.com";
            await sendInternalEmail({
              templateName: "concierge-under-review",
              recipientEmail: clientEmail,
              idempotencyKey: conciergeEmailKey("under_review", data.id),
              templateData: {
                destination: row.destination,
                requestUrl: `${origin}/concierge/${data.id}`,
                lang: await fetchConciergeLang(context.supabase, data.id),
              },
            });
          }
        }
      } catch (e) {
        console.warn("under-review email failed", e);
      }
    }

    return { ok: true as const, unchanged: false as const };
  });

// ─── Novos: preço, checkout, entrega ──────────────────────────────────────

/** Orçamento ativo (não pago, não substituído) de um pedido. */
async function loadActiveQuote(
  supabase: unknown,
  requestId: string,
): Promise<ConciergeSnapshot["activeQuote"]> {
  const sb = supabase as {
    from: (t: string) => {
      select: (c: string) => {
        eq: (
          col: string,
          v: string,
        ) => {
          in: (
            col: string,
            v: string[],
          ) => {
            order: (
              col: string,
              o: { ascending: boolean },
            ) => {
              limit: (n: number) => Promise<{
                data: Array<{ status: string; expires_at: string | null }> | null;
              }>;
            };
          };
        };
      };
    };
  };
  try {
    const { data } = await sb
      .from("concierge_quotes")
      .select("status,expires_at")
      .eq("concierge_request_id", requestId)
      .in("status", ["quoted", "checkout_created"])
      .order("version", { ascending: false })
      .limit(1);
    const q = data?.[0];
    return q ? { status: q.status, expiresAt: q.expires_at } : null;
  } catch {
    return null;
  }
}

async function assertAdmin(supabase: unknown, userId: string) {
  const sb = supabase as {
    rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown }>;
  };
  const { data } = await sb.rpc("is_admin", { _user_id: userId });
  if (!data) throw new Error("Forbidden");
}

// Admin OU concierge_agent atribuído ao pedido
async function assertStaffForRequest(
  supabase: unknown,
  userId: string,
  requestId: string,
): Promise<{ isAdmin: boolean }> {
  const sb = supabase as {
    rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown }>;
    from: (t: string) => {
      select: (c: string) => {
        eq: (
          col: string,
          v: string,
        ) => { maybeSingle: () => Promise<{ data: { assigned_to: string | null } | null }> };
      };
    };
  };
  const { data: isAdmin } = await sb.rpc("is_admin", { _user_id: userId });
  if (isAdmin) return { isAdmin: true };
  const { data: isAgent } = await sb.rpc("is_concierge_agent", { _user_id: userId });
  if (!isAgent) throw new Error("Forbidden");
  const { data: row } = await sb
    .from("concierge_requests")
    .select("assigned_to")
    .eq("id", requestId)
    .maybeSingle();
  if (!row || row.assigned_to !== userId) throw new Error("Forbidden");
  return { isAdmin: false };
}

const SetPriceInput = z.object({
  id: z.string().uuid(),
  priceCents: z.number().int().min(0).max(1_000_000).nullable(),
  isFirstFree: z.boolean().default(false),
});
export const setConciergePrice = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => SetPriceInput.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data: current } = await context.supabase
      .from("concierge_requests")
      .select("status,payment_status,is_first_free,price_cents,delivered_at")
      .eq("id", data.id)
      .single();
    if (current) {
      const editable = canEditQuote({
        status: current.status,
        paymentStatus: current.payment_status ?? "",
        isFirstFree: current.is_first_free,
        priceCents: current.price_cents,
        deliveredAt: current.delivered_at,
        hasDeliverable: false,
        activeQuote: await loadActiveQuote(context.supabase, data.id),
      });
      if (!editable.ok) throw new Error(editable.reason);
    }
    const patch = {
      price_cents: data.priceCents,
      is_first_free: data.isFirstFree,
      ...(data.isFirstFree ? { payment_status: "nao_aplicavel" as const } : {}),
    };
    const { error } = await context.supabase
      .from("concierge_requests")
      .update(patch)
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/**
 * Emite um orçamento imutável (nova versão) + Checkout Session Stripe.
 * O valor, moeda, ambiente e configuração fiscal são sempre resolvidos no
 * servidor. O cliente só envia o id do pedido.
 */
export const createConciergeCheckout = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ id: z.string().uuid(), confirmSupersede: z.boolean().default(false) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data: row, error } = await context.supabase
      .from("concierge_requests")
      .select("id,user_id,destination,price_cents,is_first_free,status,payment_status,delivered_at")
      .eq("id", data.id)
      .single();
    if (error || !row) throw new Error(error?.message ?? "Not found");
    if (row.is_first_free) throw new Error("Este pedido está marcado como grátis.");
    if (!row.price_cents || row.price_cents < 100)
      throw new Error("Define um preço válido primeiro (mínimo €1).");
    const editable = canEditQuote({
      status: row.status,
      paymentStatus: row.payment_status ?? "",
      isFirstFree: row.is_first_free,
      priceCents: row.price_cents,
      deliveredAt: row.delivered_at,
      hasDeliverable: false,
      activeQuote: null,
    });
    if (!editable.ok) throw new Error(editable.reason);

    const { nextQuoteVersion, quoteExpiryIso, stripeSessionExpiresAt, validateConciergeConfig } =
      await import("@/lib/concierge-quote");
    const { loadConciergeConfig, resolveConciergeEnvironment } =
      await import("@/lib/concierge-config.server");
    const env = resolveConciergeEnvironment();
    const config = await loadConciergeConfig(env);
    const configErrors = validateConciergeConfig(config, env);
    if (configErrors.length) {
      throw new Error(`Configuração Concierge incompleta (${env}): ${configErrors.join(" ")}`);
    }

    const { data: existingQuotes } = await context.supabase
      .from("concierge_quotes")
      .select("id,version,status,checkout_session_id,environment")
      .eq("concierge_request_id", row.id);
    const quotes = existingQuotes ?? [];
    const active = quotes.filter((q) => q.status === "quoted" || q.status === "checkout_created");
    if (active.length && !data.confirmSupersede) {
      throw new Error(
        "NEEDS_CONFIRMATION: já existe um orçamento enviado; confirma a substituição.",
      );
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: userLookup } = row.user_id
      ? await supabaseAdmin.auth.admin.getUserById(row.user_id)
      : { data: null };
    const clientEmail = userLookup?.user?.email ?? null;

    const { createStripeClient, getStripeErrorMessage } = await import("@/lib/stripe.server");
    const stripe = createStripeClient(env);

    // O productId é por ambiente: um produto criado em live não existe em sandbox
    // (e vice-versa). Valida ANTES de criar/substituir orçamentos, para não deixar
    // o pedido sem orçamento ativo por causa de configuração errada.
    try {
      const product = await stripe.products.retrieve(config.productId!);
      if (product.deleted || product.active === false) {
        throw new Error(
          `O produto Concierge (${config.productId}) está inativo no ambiente ${env}. Atualiza-o no Painel do Fundador.`,
        );
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : getStripeErrorMessage(e);
      if (/No such product|resource_missing/i.test(message)) {
        throw new Error(
          `O produto Concierge configurado (${config.productId}) não existe no ambiente ${env}. Configura o Product ID deste ambiente no Painel do Fundador (Concierge — produto e IVA).`,
        );
      }
      throw new Error(message);
    }

    // Expira/cancela sessões anteriores e marca as quotes como substituídas.
    for (const prev of active) {
      if (prev.checkout_session_id && prev.environment === env) {
        try {
          await stripe.checkout.sessions.expire(prev.checkout_session_id);
        } catch (e) {
          console.warn("concierge: expire previous session failed", getStripeErrorMessage(e));
        }
      }
      await context.supabase
        .from("concierge_quotes")
        .update({ status: "superseded" })
        .eq("id", prev.id);
      await context.supabase.from("concierge_quote_events").insert({
        quote_id: prev.id,
        concierge_request_id: row.id,
        action: "superseded",
        from_status: prev.status,
        to_status: "superseded",
        actor_user_id: context.userId,
      });
    }

    const now = new Date();
    const version = nextQuoteVersion(quotes);
    const { data: quote, error: quoteErr } = await context.supabase
      .from("concierge_quotes")
      .insert({
        concierge_request_id: row.id,
        version,
        amount_cents: row.price_cents,
        currency: config.currency,
        environment: env,
        tax_behavior: config.taxBehavior!,
        stripe_product_id: config.productId!,
        stripe_tax_code: config.taxCode,
        status: "quoted",
        created_by: context.userId,
        expires_at: quoteExpiryIso(now, config.validityHours),
      })
      .select("id,version,expires_at")
      .single();
    if (quoteErr || !quote) throw new Error(quoteErr?.message ?? "Falha a criar orçamento");

    await context.supabase.from("concierge_quote_events").insert({
      quote_id: quote.id,
      concierge_request_id: row.id,
      action: "created",
      to_status: "quoted",
      actor_user_id: context.userId,
      metadata: {
        amount_cents: row.price_cents,
        currency: config.currency,
        tax_behavior: config.taxBehavior,
        environment: env,
      },
    });

    try {
      const origin = process.env.APP_PUBLIC_URL ?? "https://routify-tour.com";
      const session = await stripe.checkout.sessions.create({
        mode: "payment",
        line_items: [
          {
            price_data: {
              currency: config.currency,
              unit_amount: row.price_cents,
              product: config.productId!,
              tax_behavior: config.taxBehavior!,
            },
            quantity: 1,
          },
        ],
        automatic_tax: { enabled: true },
        // Orçamento imutável: nenhum cupão pode alterar o valor acordado.
        allow_promotion_codes: false,
        billing_address_collection: "required",
        customer_creation: "always",
        expires_at: stripeSessionExpiresAt(now, config.validityHours),
        success_url: `${origin}/concierge/${row.id}?paid=1`,
        cancel_url: `${origin}/concierge/${row.id}?cancelled=1`,
        ...(clientEmail && { customer_email: clientEmail }),
        payment_intent_data: { description: `Concierge — ${row.destination}` },
        metadata: {
          concierge_request_id: row.id,
          quote_id: quote.id,
          quote_version: String(version),
        },
      });

      await context.supabase
        .from("concierge_quotes")
        .update({
          status: "checkout_created",
          checkout_session_id: session.id,
          checkout_url: session.url,
        })
        .eq("id", quote.id);
      await context.supabase.from("concierge_quote_events").insert({
        quote_id: quote.id,
        concierge_request_id: row.id,
        action: "checkout_created",
        from_status: "quoted",
        to_status: "checkout_created",
        actor_user_id: context.userId,
      });

      await context.supabase
        .from("concierge_requests")
        .update({
          checkout_session_id: session.id,
          checkout_url: session.url,
          payment_status: "pendente",
        })
        .eq("id", row.id);

      // Notifica o cliente por email
      if (clientEmail) {
        try {
          const { sendInternalEmail } = await import("@/lib/email/send-internal.server");
          await sendInternalEmail({
            templateName: "concierge-payment-link",
            recipientEmail: clientEmail,
            idempotencyKey: conciergeEmailKey("quote_sent", row.id, version),
            templateData: {
              destination: row.destination,
              priceEur: ((row.price_cents ?? 0) / 100).toFixed(2),
              checkoutUrl: session.url,
              requestUrl: `${origin}/concierge/${row.id}`,
              lang: await fetchConciergeLang(context.supabase, row.id),
            },
          });
        } catch (e) {
          console.warn("payment-link email failed", e);
        }
      }

      return {
        checkoutUrl: session.url,
        sessionId: session.id,
        quoteId: quote.id,
        version,
        taxBehavior: config.taxBehavior,
      };
    } catch (e) {
      await context.supabase
        .from("concierge_quotes")
        .update({ status: "cancelled" })
        .eq("id", quote.id);
      await context.supabase.from("concierge_quote_events").insert({
        quote_id: quote.id,
        concierge_request_id: row.id,
        action: "checkout_failed",
        to_status: "cancelled",
        actor_user_id: context.userId,
      });
      throw new Error(getStripeErrorMessage(e));
    }
  });

const SaveDeliverableInput = z.object({
  id: z.string().uuid(),
  text: z.string().max(50_000).nullable(),
  pdfPath: z.string().max(500).nullable(),
  appRouteLink: z.string().max(500).nullable(),
});
export const saveConciergeDeliverable = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => SaveDeliverableInput.parse(d))
  .handler(async ({ data, context }) => {
    await assertStaffForRequest(context.supabase, context.userId, data.id);
    const patch: {
      deliverable_text?: string | null;
      deliverable_pdf_url?: string | null;
      app_route_link?: string | null;
    } = {};
    if (data.text !== null) patch.deliverable_text = data.text;
    if (data.pdfPath !== null) patch.deliverable_pdf_url = data.pdfPath;
    if (data.appRouteLink !== null) patch.app_route_link = data.appRouteLink || null;
    const { error } = await context.supabase
      .from("concierge_requests")
      .update(patch)
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const createDeliverableUploadUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        filename: z.string().max(200),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertStaffForRequest(context.supabase, context.userId, data.id);
    const safeName = data.filename.replace(/[^a-zA-Z0-9._-]/g, "_");
    // Unpredictable filename: request_id prefix (RLS-enforced) + random token
    // prevents path guessing while keeping ownership scoping intact.
    const randomToken = crypto.randomUUID().replace(/-/g, "");
    const path = `${data.id}/${randomToken}-${safeName}`;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: signed, error } = await supabaseAdmin.storage
      .from("concierge-deliverables")
      .createSignedUploadUrl(path);
    if (error) throw new Error(error.message);
    return { path, token: signed.token, signedUrl: signed.signedUrl };
  });

export const deliverConcierge = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertStaffForRequest(context.supabase, context.userId, data.id);
    const { data: row, error } = await context.supabase
      .from("concierge_requests")
      .select(
        "id,user_id,destination,status,payment_status,is_first_free,delivered_at,deliverable_text,deliverable_pdf_url,app_route_link",
      )
      .eq("id", data.id)
      .single();
    if (error || !row) throw new Error(error?.message ?? "Not found");

    const snapshot: ConciergeSnapshot = {
      status: row.status,
      paymentStatus: row.payment_status ?? "",
      isFirstFree: row.is_first_free,
      priceCents: null,
      deliveredAt: row.delivered_at,
      hasDeliverable: Boolean(
        row.deliverable_text || row.deliverable_pdf_url || row.app_route_link,
      ),
      activeQuote: await loadActiveQuote(context.supabase, data.id),
    };
    const check = checkDeliverable(snapshot);
    // Entrega exatamente uma vez: repetir não reenvia nem reescreve a data.
    if (!check.ok && check.reason === "already_delivered") {
      return { ok: true as const, alreadyDelivered: true as const };
    }
    if (!check.ok) throw new Error(check.reason);

    const { error: updErr } = await context.supabase
      .from("concierge_requests")
      .update({
        delivered_at: new Date().toISOString(),
        status: "entregue",
      })
      .eq("id", row.id)
      .is("delivered_at", null);
    if (updErr) throw new Error(updErr.message);

    try {
      await context.supabase.from("concierge_status_history").insert({
        request_id: row.id,
        from_status: row.status,
        to_status: "entregue",
        changed_by: context.userId,
        note: "Entregue ao cliente",
      });
    } catch (e) {
      console.warn("delivery history insert failed", e);
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: userLookup } = row.user_id
      ? await supabaseAdmin.auth.admin.getUserById(row.user_id)
      : { data: null };
    const clientEmail = userLookup?.user?.email ?? null;
    if (clientEmail) {
      try {
        const { sendInternalEmail } = await import("@/lib/email/send-internal.server");
        const origin = process.env.APP_PUBLIC_URL ?? "https://routify-tour.com";
        await sendInternalEmail({
          templateName: "concierge-delivered",
          recipientEmail: clientEmail,
          idempotencyKey: conciergeEmailKey("delivered", row.id),
          templateData: {
            destination: row.destination,
            requestUrl: `${origin}/concierge/${row.id}`,
            lang: await fetchConciergeLang(context.supabase, row.id),
          },
        });
      } catch (e) {
        console.warn("delivered email failed", e);
      }
    }
    return { ok: true as const, alreadyDelivered: false as const };
  });

const EDITABLE_STATUSES = new Set(["novo", "em_analise"]);

const UpdateInput = z.object({
  id: z.string().uuid(),
  destination: z.string().trim().min(2).max(200).optional(),
  start_date: z.string().optional().nullable(),
  end_date: z.string().optional().nullable(),
  people: z.number().int().min(1).max(50).optional(),
  budget: z.number().nonnegative().optional().nullable(),
  currency: z.string().max(6).optional(),
  interests: z.array(z.string().max(60)).max(30).optional(),
  transport: z.string().max(40).optional().nullable(),
  accommodation: z.string().max(200).optional().nullable(),
  notes: z.string().max(4000).optional().nullable(),
  attachments: z
    .array(z.object({ name: z.string().max(200), url: z.string().url().max(1000) }))
    .max(10)
    .optional(),
});

const EDITABLE_FIELDS = [
  "destination",
  "start_date",
  "end_date",
  "people",
  "budget",
  "currency",
  "interests",
  "transport",
  "accommodation",
  "notes",
  "attachments",
] as const;

/**
 * Client-side edit/append of the request's own submitted fields. Only
 * possible while the request is still "novo" or "em análise" — RLS also
 * enforces this, but we check it up-front for a clear, friendly error.
 * Every accepted edit is recorded (before/after + version) in
 * `concierge_request_edits` for the audit trail.
 */
export const updateConciergeRequest = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => UpdateInput.parse(d))
  .handler(async ({ data, context }) => {
    const { id, ...patchInput } = data;
    const { data: current, error: readErr } = await context.supabase
      .from("concierge_requests")
      .select("*")
      .eq("id", id)
      .eq("user_id", context.userId)
      .maybeSingle();
    if (readErr) throw new Error(readErr.message);
    if (!current) throw new Error("Not found");

    if (!EDITABLE_STATUSES.has(current.status)) {
      throw new Error(
        "Este pedido já está em desenvolvimento e deixou de poder ser editado. " +
          "Contacta a equipa Routify se precisares de alterar algo.",
      );
    }

    const patch: Record<string, unknown> = {};
    const changedFields: string[] = [];
    const before: Record<string, unknown> = {};
    const after: Record<string, unknown> = {};
    for (const field of EDITABLE_FIELDS) {
      if (!(field in patchInput)) continue;
      const nextValue = (patchInput as Record<string, unknown>)[field];
      const prevValue = (current as Record<string, unknown>)[field];
      if (JSON.stringify(nextValue) === JSON.stringify(prevValue)) continue;
      patch[field] = nextValue;
      changedFields.push(field);
      before[field] = prevValue;
      after[field] = nextValue;
    }

    if (changedFields.length === 0) return { ok: true as const, unchanged: true as const };

    const nextVersion = (current.edit_version ?? 0) + 1;
    const { error: updErr } = await context.supabase
      .from("concierge_requests")
      .update({ ...patch, edit_version: nextVersion })
      .eq("id", id)
      .eq("user_id", context.userId)
      .in("status", ["novo", "em_analise"]);
    if (updErr) throw new Error(updErr.message);

    const { error: editErr } = await context.supabase.from("concierge_request_edits").insert({
      request_id: id,
      version: nextVersion,
      edited_by: context.userId,
      changed_fields: changedFields,
      before: before as unknown as Json,
      after: after as unknown as Json,
    });
    if (editErr) console.warn("concierge edit audit insert failed", editErr.message);

    return { ok: true as const, unchanged: false as const, version: nextVersion };
  });

/** Full audit trail (edit history) for a request — owner or admin only. */
export const listConciergeRequestEdits = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { data: req } = await context.supabase
      .from("concierge_requests")
      .select("user_id")
      .eq("id", data.id)
      .maybeSingle();
    if (!req) throw new Error("Not found");
    const { data: isAdmin } = await context.supabase.rpc("is_admin", { _user_id: context.userId });
    if (req.user_id !== context.userId && !isAdmin) throw new Error("Forbidden");
    const { data: rows, error } = await context.supabase
      .from("concierge_request_edits")
      .select("*")
      .eq("request_id", data.id)
      .order("version", { ascending: false });
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

export const getMyConciergeRequest = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { data: row, error } = await context.supabase
      .from("concierge_requests")
      .select("*")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) throw new Error("Not found");
    // RLS já garante que só vê os seus, mas dupla-guarda (owner / admin / agente atribuído):
    const { data: isAdmin } = await context.supabase.rpc("is_admin", { _user_id: context.userId });
    const { data: isAgent } = await context.supabase.rpc("is_concierge_agent", {
      _user_id: context.userId,
    });
    const isAssignedAgent = Boolean(isAgent) && row.assigned_to === context.userId;
    if (row.user_id !== context.userId && !isAdmin && !isAssignedAgent)
      throw new Error("Forbidden");

    // Signed URL para o PDF se entregue + (pago ou grátis)
    let pdfSignedUrl: string | null = null;
    const isDelivered = Boolean(row.delivered_at) || row.status === "entregue";
    const isPaid =
      row.is_first_free || row.payment_status === "pago" || row.payment_status === "nao_aplicavel";
    if (isDelivered && row.deliverable_pdf_url && (isPaid || row.status === "entregue")) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: signed } = await supabaseAdmin.storage
        .from("concierge-deliverables")
        .createSignedUrl(row.deliverable_pdf_url, 60 * 60 * 2);
      pdfSignedUrl = signed?.signedUrl ?? null;
    }
    return {
      ...row,
      pdfSignedUrl,
      active_quote: await loadActiveQuote(context.supabase, row.id),
    };
  });

// ─── Arquivar / desarquivar pedidos (admin) ───────────────────────────────

const ArchiveInput = z.object({
  id: z.string().uuid(),
  archived: z.boolean(),
});

export const setConciergeArchived = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => ArchiveInput.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const patch = data.archived ? { archived_at: new Date().toISOString() } : { archived_at: null };
    const { error } = await context.supabase
      .from("concierge_requests")
      .update(patch)
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

const DeleteInput = z.object({ id: z.string().uuid() });

export const deleteConciergeRequest = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => DeleteInput.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data: row } = await context.supabase
      .from("concierge_requests")
      .select("deliverable_pdf_url")
      .eq("id", data.id)
      .maybeSingle();
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    if (row?.deliverable_pdf_url) {
      await supabaseAdmin.storage.from("concierge-deliverables").remove([row.deliverable_pdf_url]);
    }
    await supabaseAdmin.from("concierge_status_history").delete().eq("request_id", data.id);
    const { error } = await supabaseAdmin.from("concierge_requests").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ─── Colaboradores (concierge_agent) — admin only ────────────────────────

export const listConciergeAgents = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: roles } = await supabaseAdmin
      .from("user_roles")
      .select("user_id")
      .eq("role", "concierge_agent");
    const ids = Array.from(new Set((roles ?? []).map((r: { user_id: string }) => r.user_id)));
    if (!ids.length)
      return { agents: [] as Array<{ id: string; email: string; displayName: string | null }> };
    const [{ data: profs }, emailPairs] = await Promise.all([
      supabaseAdmin.from("profiles").select("id, display_name").in("id", ids),
      Promise.all(
        ids.map(async (uid) => {
          const { data: u } = await supabaseAdmin.auth.admin.getUserById(uid);
          return [uid, u?.user?.email ?? ""] as const;
        }),
      ),
    ]);
    const nameMap = new Map(
      (profs ?? []).map((p: { id: string; display_name: string | null }) => [p.id, p.display_name]),
    );
    const emailMap = new Map(emailPairs);
    return {
      agents: ids.map((id) => ({
        id,
        email: emailMap.get(id) ?? "",
        displayName: nameMap.get(id) ?? null,
      })),
    };
  });

export const grantConciergeAgent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ userId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("user_roles")
      .insert({ user_id: data.userId, role: "concierge_agent" });
    if (error && !String(error.message).toLowerCase().includes("duplicate"))
      throw new Error(error.message);
    return { ok: true };
  });

export const revokeConciergeAgent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ userId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // Ao remover o papel, também limpa pedidos atribuídos a este colaborador.
    await supabaseAdmin
      .from("concierge_requests")
      .update({ assigned_to: null })
      .eq("assigned_to", data.userId);
    const { error } = await supabaseAdmin
      .from("user_roles")
      .delete()
      .eq("user_id", data.userId)
      .eq("role", "concierge_agent");
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const assignConciergeRequest = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        agentId: z.string().uuid().nullable(),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    if (data.agentId) {
      // Confirma que o alvo é mesmo um concierge_agent
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: r } = await supabaseAdmin
        .from("user_roles")
        .select("role")
        .eq("user_id", data.agentId)
        .eq("role", "concierge_agent")
        .maybeSingle();
      if (!r) throw new Error("Utilizador não é um colaborador de concierge.");
    }
    const { error } = await context.supabase
      .from("concierge_requests")
      .update({ assigned_to: data.agentId })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/**
 * Reconciliação manual (apenas fundador): marca um pedido como pago quando o
 * webhook Stripe falhou. Fica registado no histórico com motivo obrigatório.
 */
export const reconcileConciergePayment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        reason: z.string().trim().min(5).max(500),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data: row, error } = await context.supabase
      .from("concierge_requests")
      .select("id,status,payment_status,is_first_free")
      .eq("id", data.id)
      .single();
    if (error || !row) throw new Error(error?.message ?? "Not found");
    if (row.payment_status === "pago") {
      return { ok: true as const, unchanged: true as const };
    }

    const { error: updErr } = await context.supabase
      .from("concierge_requests")
      .update({ payment_status: "pago" })
      .eq("id", row.id);
    if (updErr) throw new Error(updErr.message);

    await context.supabase.from("concierge_status_history").insert({
      request_id: row.id,
      from_status: row.status,
      to_status: row.status,
      changed_by: context.userId,
      note: `RECONCILIAÇÃO MANUAL de pagamento: ${data.reason}`,
    });
    return { ok: true as const, unchanged: false as const };
  });
